/**
 * Lease Status Component
 *
 * Shows recent move-ins and their lease signing status.
 * Helps verify if new tenants have countersigned their lease
 * before giving them gate access.
 */

import { useState, useEffect } from 'react'
import {
  FileCheck, FileX, FileClock, Search, Loader2, AlertCircle,
  CheckCircle, XCircle, Clock, User, Calendar, Home, RefreshCw, Bug, X
} from 'lucide-react'
import { api } from '../lib/api'

// Status badge configurations
const statusConfig = {
  signed: {
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle,
    iconColor: 'text-green-500'
  },
  pending: {
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock,
    iconColor: 'text-amber-500'
  },
  unsigned: {
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    icon: FileClock,
    iconColor: 'text-orange-500'
  },
  missing: {
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: XCircle,
    iconColor: 'text-red-500'
  },
  unknown: {
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    icon: AlertCircle,
    iconColor: 'text-gray-400'
  }
}

export default function LeaseStatus({ facilityId, facilityName }) {
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [searching, setSearching] = useState(false)
  const [totalFound, setTotalFound] = useState(0)
  const [showDebug, setShowDebug] = useState(false)
  const [debugData, setDebugData] = useState(null)
  const [loadingDebug, setLoadingDebug] = useState(false)
  const [displayLimit, setDisplayLimit] = useState(5)

  // Fetch recent move-ins on mount and when facility or limit changes
  useEffect(() => {
    if (facilityId) {
      fetchRecentMoveIns()
    }
  }, [facilityId, displayLimit])

  const fetchRecentMoveIns = async () => {
    setLoading(true)
    setError(null)
    setSearchResults(null)
    setSearchQuery('')

    try {
      const data = await api.get(`/facilities/${facilityId}/lease-status?limit=${displayLimit}`)
      setTenants(data.tenants || [])
      setTotalFound(data.totalFound || 0)
    } catch (err) {
      console.error('Failed to fetch lease status:', err)
      setError(err.message || 'Failed to load lease status')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async (e) => {
    e.preventDefault()
    if (!searchQuery.trim()) {
      setSearchResults(null)
      return
    }

    setSearching(true)
    setError(null)

    try {
      const data = await api.get(`/facilities/${facilityId}/lease-status?search=${encodeURIComponent(searchQuery)}&limit=10`)
      setSearchResults(data.tenants || [])
    } catch (err) {
      console.error('Search failed:', err)
      setError(err.message || 'Search failed')
    } finally {
      setSearching(false)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults(null)
  }

  const fetchDebugData = async () => {
    setLoadingDebug(true)
    try {
      const data = await api.get(`/facilities/${facilityId}/lease-status?debug=true&limit=1`)
      setDebugData(data.debug)
      setShowDebug(true)
    } catch (err) {
      console.error('Failed to fetch debug data:', err)
      setError('Failed to load debug data')
    } finally {
      setLoadingDebug(false)
    }
  }

  const displayTenants = searchResults !== null ? searchResults : tenants

  if (!facilityId) {
    return null
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-100">
            <FileCheck className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Lease Status</h3>
            <p className="text-xs text-gray-500">
              {searchResults !== null
                ? `Search results for "${searchQuery}"`
                : 'Recent move-ins'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={displayLimit}
            onChange={(e) => setDisplayLimit(Number(e.target.value))}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value={5}>Last 5</option>
            <option value={10}>Last 10</option>
            <option value={15}>Last 15</option>
          </select>
          <button
            onClick={fetchRecentMoveIns}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="input pl-10 pr-20 py-2 text-sm"
          />
          {searchQuery && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <button
                type="button"
                onClick={clearSearch}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
              <button
                type="submit"
                disabled={searching}
                className="px-3 py-1 bg-indigo-500 text-white text-xs rounded-md hover:bg-indigo-600 transition-colors"
              >
                {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Search'}
              </button>
            </div>
          )}
        </div>
      </form>

      {/* Error State */}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2 mb-4">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
        </div>
      )}

      {/* Results */}
      {!loading && displayTenants.length > 0 && (
        <div className="space-y-2">
          {displayTenants.map((tenant) => {
            const config = statusConfig[tenant.leaseStatus] || statusConfig.unknown
            const StatusIcon = config.icon

            return (
              <div
                key={tenant.id}
                className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <p className="font-medium text-gray-900 truncate">{tenant.name}</p>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      {tenant.unit && (
                        <span className="flex items-center gap-1">
                          <Home className="w-3 h-3" />
                          {tenant.unit}
                        </span>
                      )}
                      {tenant.moveInDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(tenant.moveInDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
                      <StatusIcon className={`w-3 h-3 ${config.iconColor}`} />
                      {tenant.leaseStatus === 'signed' ? 'Signed' :
                       tenant.leaseStatus === 'pending' ? 'Pending' :
                       tenant.leaseStatus === 'unsigned' ? 'Unsigned' :
                       tenant.leaseStatus === 'missing' ? 'Missing' : 'Unknown'}
                    </span>
                    <p className="text-xs text-gray-400">{tenant.statusDetail}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && displayTenants.length === 0 && (
        <div className="text-center py-6">
          {searchResults !== null ? (
            <>
              <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No tenants found matching "{searchQuery}"</p>
            </>
          ) : (
            <>
              <FileCheck className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No recent move-ins found</p>
            </>
          )}
        </div>
      )}

      {/* Footer - show count when not searching */}
      {!loading && !searchResults && tenants.length > 0 && totalFound > displayLimit && (
        <p className="text-xs text-gray-400 text-center mt-3">
          Showing {displayLimit} of {totalFound} recent move-ins
        </p>
      )}

      {/* Debug button */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <button
          onClick={fetchDebugData}
          disabled={loadingDebug}
          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
        >
          {loadingDebug ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bug className="w-3 h-3" />}
          Debug API Data
        </button>
      </div>

      {/* Debug Modal */}
      {showDebug && debugData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">API Debug Info</h2>
              <button
                onClick={() => setShowDebug(false)}
                className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(80vh-60px)]">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    Total Ledgers: {debugData.ledgerCount} | Current (not moved out): {debugData.currentLedgerCount}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Active Field Values:</p>
                  <pre className="text-xs bg-gray-100 p-3 rounded-lg overflow-auto">
                    {JSON.stringify(debugData.activeValueCounts, null, 2) || 'No data'}
                  </pre>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Ledger Field Names:</p>
                  <pre className="text-xs bg-gray-100 p-3 rounded-lg overflow-auto">
                    {debugData.sampleLedgerKeys?.join(', ') || 'No ledger data'}
                  </pre>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Most Recent Ledger (all):</p>
                  <pre className="text-xs bg-gray-100 p-3 rounded-lg overflow-auto max-h-60">
                    {JSON.stringify(debugData.mostRecentLedger, null, 2) || 'No ledger data'}
                  </pre>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Most Recent Current Ledger:</p>
                  <pre className="text-xs bg-gray-100 p-3 rounded-lg overflow-auto max-h-60">
                    {JSON.stringify(debugData.mostRecentCurrentLedger, null, 2) || 'No ledger data'}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
